import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import type { RenderableConfigFormController } from '@adornis/buildify/client/RenderableConfigFormController.js';
import type { Base } from '@adornis/buildify/db/Base.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { DubniumBaseSupportFormular } from 'db/buildify/_formulars/DubniumBaseSupportFormular.js';
import { Product } from 'db/product/Product.js';
import { getAllActiveProducts } from 'db/product/queries/getAllActiveProducts.js';
import { SupportTopic } from 'formulars/_support-form/db/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TranslationRootItem } from 'translation/enums.js';
import './campus-support-formular-extension.js';

export const BuildifyCampusSupportFormularExtension: BuildifyExtension<DubniumBaseSupportFormular> = {
  class: DubniumBaseSupportFormular,
  toolbar: { icon: 'headset', text: { default: 'Supportformular' } },
  render(instance, { defaultValues, isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <campus-support-formular-extension
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></campus-support-formular-extension>
    `;
  },
  editor: ({ content, contentController, controllerBaseKeyPath }) => {
    return html`
      <!-- topic -->
      <campus-support-formular-editor
        .content=${content}
        .contentController=${contentController}
        .controllerBaseKeyPath=${controllerBaseKeyPath}
      ></campus-support-formular-editor>
    `;
  },
  group: 'Formulare',
};

@customElement('campus-support-formular-editor')
export class CampusSupportFormularEditor extends ChemistryLitElement {
  @property({ attribute: false }) content!: DubniumBaseSupportFormular;
  @property({ attribute: false }) contentController!: RenderableConfigFormController<Base>;
  @property({ attribute: false }) controllerBaseKeyPath!: (string | number)[];

  @state() private readonly _products = new RXController(this, getAllActiveProducts()(Product.allFields));

  override render() {
    return html`
      <d-flex space="md">
        <d-dropdown-selection
          clearable
          placeholder=${'Thema'}
          .selectables=${Object.keys(SupportTopic)}
          .renderString=${topicKey =>
            clientTranslator.translate(TranslationRootItem.SUPPORT_FORMULAR, 'topic', topicKey)}
          ${this.contentController.field(...this.controllerBaseKeyPath, 'topic')}
          @value-picked=${(e: ValueEvent<Maybe<SupportTopic>>) => {
            if (
              e.detail.value !== SupportTopic.INTEREST_IN_PRODUCT &&
              e.detail.value !== SupportTopic.TECHNICAL_ISSUE
            ) {
              this.content.productID = null;
            }
            this.requestUpdate();
          }}
        ></d-dropdown-selection>

        <!-- product -->
        ${this.content.topic &&
        [SupportTopic.INTEREST_IN_PRODUCT, SupportTopic.TECHNICAL_ISSUE].includes(this.content.topic) &&
        this._products.value
          ? html`
              <d-dropdown-selection
                clearable
                placeholder=${this.content.topic === SupportTopic.INTEREST_IN_PRODUCT
                  ? 'Um welches Produkt handelt es sich?'
                  : 'Hat dieses Problem mit einem dieser Produkte zu tun?'}
                .selectables=${this._products.value.map(p => p.id)}
                .renderString=${id => this._products.value!.find(p => p.id === id)!.name}
                ${this.contentController.field(...this.controllerBaseKeyPath, 'productID')}
              ></d-dropdown-selection>
            `
          : nothing}
      </d-flex>
    `;
  }
}
